import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Alert,
  Divider,
  Stack,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import api from "../api/api";

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 360,
      width: 320,
    },
  },
};

function formatValue(value, casas = 1) {
  if (value == null || !Number.isFinite(Number(value))) return "--";
  return Number(value).toFixed(casas).replace(".", ",");
}

function nowHHMMSS() {
  return new Date().toLocaleTimeString("pt-BR", { hour12: false });
}

function normalizarDados(all = {}) {
  const obj = { ...all };

  if (obj["PM2.5"] != null && obj["PM2_5"] == null) obj["PM2_5"] = obj["PM2.5"];
  if (obj["pressao"] == null && obj["Pressao"] != null) obj["pressao"] = obj["Pressao"];

  const isMed = obj["HCHO"] != null;
  if (isMed && obj["VOC_ppm"] == null && obj["VOC"] != null) obj["VOC_ppm"] = obj["VOC"];

  if (obj["CO"] == null && obj["co"] != null) obj["CO"] = obj["co"];
  if (obj["H2S"] == null && obj["h2s"] != null) obj["H2S"] = obj["h2s"];

  return obj;
}

function detectarModelo(dados) {
  const hasHCHO = dados?.HCHO != null;
  const hasCO = dados?.CO != null || dados?.co != null;
  const hasH2S = dados?.H2S != null || dados?.h2s != null;

  if (hasHCHO) return "MED";
  if (hasCO || hasH2S) return "PRO";
  return "HABITAT";
}

function modeloTitulo(m) {
  if (m === "MED") return "Sense Med";
  if (m === "PRO") return "Sense Pro";
  return "Sense Habitat";
}

function statusAtualizacao(lastUpdateStr) {
  if (!lastUpdateStr) return { label: "Sem atualização", color: "default" };
  return { label: `Atualizado às ${lastUpdateStr}`, color: "info" };
}

const NBR = {
  deltaCO2Max: 700,
  pm10_24h: 50,
  pm25_24h: 25,
  tempMin: 21,
  tempMax: 26,
  rhMin: 35,
  rhMax: 65,
};

const PERFIS_EQUIPAMENTO = {
  HABITAT: ["CO2", "Temp", "Umid", "PM2_5", "VOC", "NOx"],
  MED: ["CO2", "Temp", "Umid", "PM2_5", "VOC_ppm", "HCHO"],
  PRO: ["Temp", "Umid", "PM2_5", "CO", "H2S", "VOC", "NOx"],
};

function faixaNBR(paramKey, value, ctx = {}) {
  if (value == null || !Number.isFinite(value)) {
    return { cor: "#9e9e9e", rotulo: "Sem dado", nivel: 0 };
  }

  if (paramKey === "CO2") {
    const { co2Out } = ctx;
    if (Number.isFinite(co2Out)) {
      const excesso = Math.max(0, value - co2Out);
      if (excesso <= NBR.deltaCO2Max) return { cor: "#43a047", rotulo: "Conforme", nivel: 1 };
      if (excesso <= NBR.deltaCO2Max + 200) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
      return { cor: "#d32f2f", rotulo: "Não conforme", nivel: 3 };
    }

    if (value <= 1000) return { cor: "#43a047", rotulo: "Bom", nivel: 1 };
    if (value <= 1200) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Elevado", nivel: 3 };
  }

  if (paramKey === "PM2_5") {
    if (value <= NBR.pm25_24h) return { cor: "#43a047", rotulo: "Conforme", nivel: 1 };
    if (value <= 35) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Não conforme", nivel: 3 };
  }

  if (paramKey === "PM10") {
    if (value <= NBR.pm10_24h) return { cor: "#43a047", rotulo: "Conforme", nivel: 1 };
    if (value <= 80) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Não conforme", nivel: 3 };
  }

  if (paramKey === "Temp") {
    if (value >= NBR.tempMin && value <= NBR.tempMax) return { cor: "#43a047", rotulo: "Recomendável", nivel: 1 };
    if (value >= 20 && value <= 27) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Fora da faixa", nivel: 3 };
  }

  if (paramKey === "Umid") {
    if (value >= NBR.rhMin && value <= NBR.rhMax) return { cor: "#43a047", rotulo: "Recomendável", nivel: 1 };
    if ((value >= 30 && value < NBR.rhMin) || (value > NBR.rhMax && value <= 70)) {
      return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    }
    return { cor: "#d32f2f", rotulo: "Fora da faixa", nivel: 3 };
  }

  if (paramKey === "VOC") {
    if (value <= 100) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 200) return { cor: "#fbc02d", rotulo: "Moderada", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Ruim", nivel: 3 };
  }

  if (paramKey === "NOx") {
    if (value <= 100) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 200) return { cor: "#fbc02d", rotulo: "Moderada", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Ruim", nivel: 3 };
  }

  if (paramKey === "VOC_ppm") {
    if (value <= 5) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 20) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Ruim", nivel: 3 };
  }

  if (paramKey === "HCHO") {
    if (value <= 0.1) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 0.2) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Ruim", nivel: 3 };
  }

  if (paramKey === "CO") {
    if (value <= 9) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 11) return { cor: "#fbc02d", rotulo: "Moderada", nivel: 2 };
    if (value <= 13) return { cor: "#ef6c00", rotulo: "Ruim", nivel: 3 };
    if (value <= 15) return { cor: "#d32f2f", rotulo: "Muito ruim", nivel: 3 };
    return { cor: "#b71c1c", rotulo: "Péssima", nivel: 3 };
  }

  if (paramKey === "H2S") {
    if (value <= 5) return { cor: "#43a047", rotulo: "Boa", nivel: 1 };
    if (value <= 8) return { cor: "#fbc02d", rotulo: "Atenção", nivel: 2 };
    return { cor: "#d32f2f", rotulo: "Acima NR-15", nivel: 3 };
  }

  return { cor: "#1976d2", rotulo: "Informativo", nivel: 1 };
}

function indicadorGeral(dados, refExterno) {
  const checks = [];
  const modelo = detectarModelo(dados);

  const add = (key, value, ctx = {}) => {
    if (value == null || !Number.isFinite(Number(value))) return;
    checks.push(faixaNBR(key, Number(value), ctx));
  };

  if (modelo !== "PRO") {
    add("CO2", dados?.CO2, { co2Out: refExterno?.CO2 });
  }

  add("PM2_5", dados?.PM2_5);
  add("PM10", dados?.PM10);
  add("Temp", dados?.Temp);
  add("Umid", dados?.Umid);

  if (dados?.VOC_ppm != null) add("VOC_ppm", dados?.VOC_ppm);
  else if (dados?.VOC != null) add("VOC", dados?.VOC);

  if (dados?.NOx != null) add("NOx", dados?.NOx);
  if (dados?.HCHO != null) add("HCHO", dados?.HCHO);
  if (dados?.CO != null) add("CO", dados?.CO);
  if (dados?.H2S != null) add("H2S", dados?.H2S);

  if (!checks.length) return { label: "Sem dados", color: "#9e9e9e", nivel: 0 };

  const worst = Math.max(...checks.map((c) => c.nivel));
  if (worst <= 1) return { label: "Bom", color: "#43a047", nivel: 1 };
  if (worst === 2) return { label: "Atenção", color: "#fbc02d", nivel: 2 };
  return { label: "Crítico", color: "#d32f2f", nivel: 3 };
}

function parametrosResumo(dados) {
  const modelo = detectarModelo(dados);
  const perfil = PERFIS_EQUIPAMENTO[modelo] || PERFIS_EQUIPAMENTO.HABITAT;

  const definicoes = {
    CO2: { key: "CO2", label: "CO₂", unidade: "ppm" },
    Temp: { key: "Temp", label: "Temp", unidade: "°C" },
    Umid: { key: "Umid", label: "Umid", unidade: "%" },
    PM2_5: { key: "PM2_5", label: "MP2.5", unidade: "µg/m³" },
    VOC_ppm: { key: "VOC_ppm", label: "VOC", unidade: "ppm" },
    HCHO: { key: "HCHO", label: "HCHO", unidade: "mg/m³" },
    CO: { key: "CO", label: "CO", unidade: "ppm" },
    H2S: { key: "H2S", label: "H₂S", unidade: "ppm" },
    VOC: { key: "VOC", label: "VOC", unidade: "index" },
    NOx: { key: "NOx", label: "NOx", unidade: "index" },
  };

  return perfil
    .filter((key) => definicoes[key])
    .map((key) => definicoes[key]);
}

export default function CCOPage({
  equipments = [],
  selectedMacOut = "",
  ambienteNomeOut = "",
  onAbrirPainel,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selecionados, setSelecionados] = useState([]);
  const [dadosMap, setDadosMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const [filtroNivel, setFiltroNivel] = useState("todos"); // todos|bom|atencao|critico
  const [ordenacao, setOrdenacao] = useState("criticidade"); // criticidade|nome|modelo
  const [compacto, setCompacto] = useState(false);

  const [ultimaSincronizacao, setUltimaSincronizacao] = useState("");
  const [refreshCountdown, setRefreshCountdown] = useState(30);

  useEffect(() => {
    if (!equipments.length) return;
    setSelecionados((prev) => (prev.length ? prev : equipments.map((e) => e.mac)));
  }, [equipments]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setErro("");
      const res = await api.get("/data");
      const raw = res.data || {};

      const obj = {};
      Object.entries(raw).forEach(([mac, dados]) => {
        obj[mac] = normalizarDados(dados);
      });

      setDadosMap(obj);
      setUltimaSincronizacao(nowHHMMSS());
      setRefreshCountdown(30);
    } catch (e) {
      setErro("Não foi possível carregar os dados do CCO.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => {
      setRefreshCountdown((prev) => (prev > 0 ? prev - 1 : 30));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const equipamentoMap = useMemo(() => {
    const m = {};
    equipments.forEach((e) => {
      m[e.mac] = e;
    });
    return m;
  }, [equipments]);

  const cardsBase = useMemo(() => {
    return selecionados.map((mac) => {
      const dados = normalizarDados(dadosMap[mac] || {});
      const eq = equipamentoMap[mac] || { mac, location: mac };
      const modelo = detectarModelo(dados);
      const refExterno = selectedMacOut ? normalizarDados(dadosMap[selectedMacOut] || {}) : null;
      const indicador = indicadorGeral(dados, refExterno);
      const resumo = parametrosResumo(dados);
      const st = statusAtualizacao(dados?._lastUpdate);

      return {
        mac,
        nome: eq.location || mac,
        modelo,
        modeloTitulo: modeloTitulo(modelo),
        dados,
        resumo,
        indicador,
        statusAtualizacao: st,
      };
    });
  }, [selecionados, dadosMap, equipamentoMap, selectedMacOut]);

  const cards = useMemo(() => {
    let arr = [...cardsBase];

    if (filtroNivel !== "todos") {
      arr = arr.filter((c) => {
        if (filtroNivel === "bom") return c.indicador.label === "Bom";
        if (filtroNivel === "atencao") return c.indicador.label === "Atenção";
        if (filtroNivel === "critico") return c.indicador.label === "Crítico";
        return true;
      });
    }

    arr.sort((a, b) => {
      if (ordenacao === "nome") {
        return a.nome.localeCompare(b.nome, "pt-BR");
      }

      if (ordenacao === "modelo") {
        const m = a.modeloTitulo.localeCompare(b.modeloTitulo, "pt-BR");
        if (m !== 0) return m;
        return a.nome.localeCompare(b.nome, "pt-BR");
      }

      // criticidade
      if (b.indicador.nivel !== a.indicador.nivel) return b.indicador.nivel - a.indicador.nivel;
      return a.nome.localeCompare(b.nome, "pt-BR");
    });

    return arr;
  }, [cardsBase, filtroNivel, ordenacao]);

  const totals = useMemo(() => {
    const total = cardsBase.length;
    const criticos = cardsBase.filter((c) => c.indicador.label === "Crítico").length;
    const atencao = cardsBase.filter((c) => c.indicador.label === "Atenção").length;
    const bons = cardsBase.filter((c) => c.indicador.label === "Bom").length;
    return { total, criticos, atencao, bons };
  }, [cardsBase]);

  const handleSelectAll = () => setSelecionados(equipments.map((e) => e.mac));
  const handleClearAll = () => setSelecionados([]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 800, mb: 1 }}>
          Centro de Controle de Operações
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Visão consolidada dos ambientes monitorados, com foco operacional e acesso rápido ao painel detalhado.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip label={`Ambientes: ${totals.total}`} color="default" variant="outlined" />
          <Chip label={`Bom: ${totals.bons}`} sx={{ bgcolor: "#43a047", color: "#fff" }} />
          <Chip label={`Atenção: ${totals.atencao}`} sx={{ bgcolor: "#fbc02d", color: "#000" }} />
          <Chip label={`Crítico: ${totals.criticos}`} sx={{ bgcolor: "#d32f2f", color: "#fff" }} />
          <Chip
            label={ultimaSincronizacao ? `Última sincronização: ${ultimaSincronizacao}` : "Sincronizando..."}
            color="info"
            variant="outlined"
          />
          <Chip
            label={`Próxima atualização em ${refreshCountdown}s`}
            color="secondary"
            variant="outlined"
          />
        </Stack>

        {!!selectedMacOut && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Referência externa global ativa: <b>{ambienteNomeOut || selectedMacOut}</b>
          </Alert>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <FormControl sx={{ minWidth: 320, flex: 1 }}>
            <InputLabel>Ambientes exibidos no CCO</InputLabel>
            <Select
              multiple
              value={selecionados}
              onChange={(e) => setSelecionados(e.target.value)}
              input={<OutlinedInput label="Ambientes exibidos no CCO" />}
              renderValue={(selected) => `${selected.length} ambiente(s) selecionado(s)`}
              MenuProps={MENU_PROPS}
            >
              {equipments.map((eq) => (
                <MenuItem key={eq.mac} value={eq.mac}>
                  <Checkbox checked={selecionados.indexOf(eq.mac) > -1} />
                  <ListItemText primary={eq.location || eq.mac} secondary={eq.mac} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={handleSelectAll}>
            Selecionar todos
          </Button>

          <Button variant="outlined" color="warning" onClick={handleClearAll}>
            Limpar
          </Button>

          <Button variant="contained" onClick={fetchData}>
            Atualizar
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Filtrar nível</InputLabel>
            <Select
              value={filtroNivel}
              label="Filtrar nível"
              onChange={(e) => setFiltroNivel(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="bom">Bom</MenuItem>
              <MenuItem value="atencao">Atenção</MenuItem>
              <MenuItem value="critico">Crítico</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Ordenar por</InputLabel>
            <Select
              value={ordenacao}
              label="Ordenar por"
              onChange={(e) => setOrdenacao(e.target.value)}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="criticidade">Criticidade</MenuItem>
              <MenuItem value="nome">Nome do ambiente</MenuItem>
              <MenuItem value="modelo">Modelo</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={compacto ? "compacto" : "normal"}
            exclusive
            onChange={(_, v) => {
              if (!v) return;
              setCompacto(v === "compacto");
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ToggleButton value="normal">Visual normal</ToggleButton>
            <ToggleButton value="compacto">Visual compacto</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {!selecionados.length ? (
        <Alert severity="info">Selecione pelo menos um ambiente para exibir no CCO.</Alert>
      ) : !cards.length ? (
        <Alert severity="info">Nenhum ambiente corresponde ao filtro atual.</Alert>
      ) : (
        <Grid container spacing={2}>
          {cards.map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={card.mac}>
              <Paper sx={{ p: compacto ? 1.4 : 2, borderRadius: 3, height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                      {card.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.mac}
                    </Typography>
                  </Box>

                  <Chip
                    label={card.indicador.label}
                    size="small"
                    sx={{ bgcolor: card.indicador.color, color: "#fff", fontWeight: 800 }}
                  />
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip label={card.modeloTitulo} size="small" color="primary" variant="outlined" />
                  <Chip
                    label={card.statusAtualizacao.label}
                    size="small"
                    color={card.statusAtualizacao.color}
                    variant="outlined"
                  />
                </Stack>

                <Divider sx={{ my: 1.3 }} />

                <Stack spacing={compacto ? 0.55 : 0.8}>
                  {card.resumo.map((p) => {
                    const raw = card.dados?.[p.key];
                    if (raw == null || !Number.isFinite(Number(raw))) return null;

                    const ctx =
                      p.key === "CO2" && selectedMacOut
                        ? { co2Out: dadosMap[selectedMacOut]?.CO2 }
                        : {};

                    const fx = faixaNBR(p.key, Number(raw), ctx);

                    return (
                      <Box
                        key={p.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {p.label}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: fx.cor }}>
                            {formatValue(raw)} {p.unidade}
                          </Typography>
                          <Chip
                            label={fx.rotulo}
                            size="small"
                            sx={{ bgcolor: fx.cor, color: "#fff", fontWeight: 700 }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                <Divider sx={{ my: 1.3 }} />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => onAbrirPainel?.(card.mac, card.nome)}
                >
                  Abrir Painel
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Atualizando dados do CCO...
          </Typography>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        Referências do CCO: <b>ABNT NBR 17037:2023</b> para partículas, temperatura e umidade; <b>IQAr/MMA jan/2025 + Conama 506/2024</b> para CO; <b>NR-15 Anexo 11</b> para H₂S. VOC/NOx em índice, VOC em ppm e formaldeído são exibidos como <b>indicadores técnicos</b>, sem equivalência legal direta universal no formato do sensor.
      </Alert>
    </Box>
  );
}
